import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { omit } from 'lodash';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form, FormField, FormFooter } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import TermsAndConditions from './TermsAndConditions';
import RecaptchaTermsAndPolicy from './RecaptchaTermsAndPolicy';

import { registerInfluencerUser } from '../reducers/signup';
import { closeNotification, danger } from 'redux-store/reducers/common-reducer';
import { fetchCountryCodes } from 'modules/locations/reducers/worldLocation';

import { t } from 'utils/language';
import { NAME_MAX_LENGTH, equals, isEmail, isNotEmpty, isPhoneNumber, isRequired, isValidPassword, maxLength } from 'utils/validators';
import { DEFAULT_COUNTRY_CODE, sortAndUniqueItems } from 'utils/helper/common';

export class InfluencerSignUpForm extends Component {
  static propTypes = {
    language: PropTypes.string,
    dispatch: PropTypes.func.isRequired
  };

  state = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
    isModified: false,
    countryCode: DEFAULT_COUNTRY_CODE,
    countries: []
  };

  componentDidMount() {
    fetchCountryCodes().then(countries => {
      this.setState(prevState => ({ ...prevState, countries }));
    });
  }

  onSubmit = () => {
    const { dispatch, language } = this.props;
    const { countryCode, phone } = this.state;

    const payload = { ...omit(this.state, ['passwordConfirmation', 'isModified', 'countryCode', 'countries']),
      phone: `${countryCode} ${phone}` };
    payload.preferredLanguage = language;

    this.setState({
      isModified: false
    });

    dispatch(closeNotification());

    dispatch(registerInfluencerUser(payload))
      .catch(() => window.scrollTo(0, 0));
  };

  onValidationErrors = () => {
    const { dispatch } = this.props;

    dispatch(danger(t('missing_fields')));
  };

  onTextInput = (event) => {
    const input = event.target;
    this.setState({
      [input.name]: input.value,
      isModified: true
    });
  };

  handleCountryCodeChange = (items, data, selectedCountry) => {
    this.setState((prevState) => ({ ...prevState,
      countries: [...items],
      countryCode: selectedCountry.value }));
  };

  render() {
    const { isModified, countryCode, countries, phone } = this.state;

    return (
      <Form
        data={{ qa: 'access-form' }}
        onSubmit={this.onSubmit}
        onValidationErrors={this.onValidationErrors}
      >
        {
          isModified &&
            <PromptExit type="page" />
        }
        <FormField
          label={t('signup_first_name')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          validators={[
            isRequired(this.state.firstName),
            maxLength(this.state.firstName, NAME_MAX_LENGTH)
          ]}
        >
          <Input
            name="firstName"
            onChange={this.onTextInput}
            data={{ qa: 'signup-first-name' }}
          />
        </FormField>

        <FormField
          label={t('signup_last_name')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          validators={[
            isRequired(this.state.lastName),
            maxLength(this.state.lastName, NAME_MAX_LENGTH)
          ]}
        >
          <Input
            onChange={this.onTextInput}
            name="lastName"
            data={{ qa: 'signup-last-name' }}
          />
        </FormField>

        <FormField
          label={t('signup_email')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          validators={[
            isRequired(this.state.email),
            isEmail(this.state.email)
          ]}
        >
          <Input
            onChange={this.onTextInput}
            name="email"
            data={{ qa: 'signup-email' }}
          />
        </FormField>
        <FormField
          label={t('signup_mobile_number')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          validators={[
            isRequired(phone),
            maxLength(phone, NAME_MAX_LENGTH),
            isPhoneNumber(`${countryCode}${phone}`, 'This is not a valid phone number.')
          ]}
        >
          <div className="influencer-signup-info__phone_number">
            <MixedSelectDropdown
              name="signup-phone-number"
              items={sortAndUniqueItems(countries)}
              onItemsToggle={this.handleCountryCodeChange}
              withSearchable
              className="influencer-signup-info__country-select"
            />
            <Input
              onChange={this.onTextInput}
              name="phone"
              className="influencer-signup-info__phone-input"
            />
          </div>
        </FormField>
        <FormField
          label={t('signup_password')}
          labelType="highlighted"
          helpNote={t('password_should_be_strong_comment')}
          direction="column"
          showRequiredMark
          validators={[
            isNotEmpty(this.state.password),
            isValidPassword(this.state.password)
          ]}
        >
          <Input
            onChange={this.onTextInput}
            type="password"
            name="password"
            data={{ qa: 'signup-password' }}
          />
        </FormField>

        <FormField
          label={t('signup_confirm_password')}
          labelType="highlighted"
          direction="column"
          showRequiredMark
          validators={[
            isNotEmpty(this.state.passwordConfirmation),
            equals(this.state.passwordConfirmation, this.state.password, t('password_does_not_match'))
          ]}
        >
          <Input
            onChange={this.onTextInput}
            type="password"
            name="passwordConfirmation"
            data={{ qa: 'signup-password-confirmation' }}
          />
        </FormField>

        <FormFooter showRequiredMarkExplanation />

        <div className="signup__tos-container">
          <TermsAndConditions buttonLabel={t('action_button_submit')} />
          <RecaptchaTermsAndPolicy />
        </div>

        <ActionButton
          type="submit"
          data={{ qa: 'signup-submit-button' }}
        >
          {t('action_button_submit')}
        </ActionButton>
      </Form>
    );
  }
}

function mapStateToProps(state) {
  return {
    language: state.common.language
  };
}

export default connect(mapStateToProps)(InfluencerSignUpForm);
